import { buildApiOptions, shouldHandleAuthExpiry } from '~/lib/api-fetch'

/**
 * 提供 $api：統一帶 baseURL + Authorization + Fingerprint header 的 $fetch instance。
 *
 * dependsOn: ['pinia'] — 確保 Pinia plugin 先註冊，避免 onRequest 觸發時 getActivePinia() 為 null。
 * onRequest / onResponseError 內取 store 用 try/catch 防 SSR / 早期 race 拋 unhandledRejection。
 *
 * Token renewal 反應式 fallback：
 * - 401 時：若 store 有 token，await `auth.renewToken()`（in-flight singleton 保護），
 *   ofetch 透過 `retry: 1, retryStatusCodes: [401]` 自動 retry；retry 時 onRequest 會從
 *   store 重新取最新 token，所以新 Authorization header 自動帶上。
 * - renew 失敗 → clearSession + 導 /login。
 * - 注意：store 的 renewToken 用 raw $fetch（不經 $api），renew 端點自己 401 不會觸發此 hook。
 */
export default defineNuxtPlugin({
  name: 'api',
  dependsOn: ['pinia'],
  setup() {
    const config = useRuntimeConfig()
    const fingerprint = useFingerprint()

    const api = $fetch.create({
      retry: 1,
      retryStatusCodes: [401],
      retryDelay: 100,
      onRequest({ options }) {
        let token: string | null = null
        try {
          token = useAuthStore().token
        }
        catch {
          // Pinia 尚未 active 或 store 未初始化 — 仍可送出 request（後端會回 401，前端守衛接手）
        }
        const { baseURL, headers } = buildApiOptions({
          apiBase: config.public.apiBase,
          fingerprint,
          token,
        })
        options.baseURL = baseURL
        for (const [key, value] of Object.entries(headers)) {
          options.headers.set(key, value)
        }
      },
      async onResponseError({ response }) {
        try {
          const auth = useAuthStore()
          if (!shouldHandleAuthExpiry(response.status, auth.token)) return
          try {
            // in-flight singleton：並發 401 共用同一個 renew Promise
            await auth.renewToken()
            // ofetch 會自動 retry（retryStatusCodes: [401]），retry 的 onRequest 會帶新 token
          }
          catch {
            auth.clearSession()
            // SSR：不 touch vue-sonner（避免 SSR 觸發 vite dep pre-bundle 卡住），
            // 也不 fire navigateTo（middleware 內 return navigateTo 已負責，重複 call 會讓
            // SSR event loop 掛住、親測 curl /upload 帶失效 token 卡 10s）。
            // Client：AJAX 打到 401 時（middleware 之外）由 plugin 負責 toast + redirect。
            if (import.meta.client) {
              const { toast } = await import('vue-sonner')
              toast.error('登入憑證已過期，請重新登入')
              void navigateTo('/login')
            }
          }
        }
        catch {
          // Pinia 不在不該炸整個 response cycle
        }
      },
    })

    return { provide: { api } }
  },
})
